import Util from './util.js'
import Config from './config.js'
import DingDebugPanel from './dingDebugPanel.js'

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector('script[src="' + src + '"]')) {
      resolve()
      return
    }
    const s = document.createElement('script')
    s.src = src
    s.onload = resolve
    s.onerror = reject
    document.head.appendChild(s)
  })
}

function ensureDd() {
  if (window.dd) return Promise.resolve()
  return loadScript('https://g.alicdn.com/dingding/dingtalk-jsapi/2.10.3/dingtalk.open.js')
}

function getWebsiteUrl() {
  const host = location.host
  return host.indexOf('.') > 0 ? host.substring(0, host.indexOf('.')) : ''
}

function getSignUrl() {
  return location.href.split('#')[0]
}

function getFullApiUrl(path) {
  const relative = Util.getApiUrl(path)
  if (/^https?:\/\//i.test(relative)) return relative
  return location.origin + (relative.startsWith('/') ? relative : '/' + relative)
}

function isDebugEnabled() {
  try {
    if (localStorage.getItem('memberDingDebug') === '1') return true
    return new URLSearchParams(location.search).get('dingDebug') === '1'
  } catch (e) {
    return false
  }
}

function shouldUseDebugPanel() {
  return isDebugEnabled() || new URLSearchParams(location.search).get('from') === 'dingtalk'
}

function dingLog(step, payload, options) {
  if (shouldUseDebugPanel()) {
    DingDebugPanel.log(step, payload, options)
    if (isDebugEnabled()) {
      DingDebugPanel.flush(true)
    }
  }
  if (isDebugEnabled()) {
    const prefix = '[member-dingtalk]'
    if (payload !== undefined) console.log(prefix, step, payload)
    else console.log(prefix, step)
  }
}

const Dingtalk = {
  isDebugEnabled,

  isDingTalk() {
    const ua = navigator.userAgent || ''
    const fromQuery = new URLSearchParams(location.search).get('from') === 'dingtalk'
    const result = ua.indexOf('DingTalk') >= 0 || fromQuery
    dingLog('isDingTalk', { ua, fromQuery, result, host: location.host, href: location.href })
    return result
  },

  getCompanyStyle() {
    const websiteUrl = getWebsiteUrl()
    const apiPath = '/main/getCompanyStyle'
    const apiUrl = getFullApiUrl(apiPath)
    dingLog('getCompanyStyle.request', { apiUrl, websiteUrl, host: location.host })
    if (!websiteUrl) {
      return Promise.reject(new Error('无法识别企业域名'))
    }
    return Util.ajax({
      data: { websiteUrl },
      url: Util.getApiUrl(apiPath)
    }, null, true).then((company) => {
      dingLog('getCompanyStyle.response', { apiUrl, data: company })
      return company
    }).catch((err) => {
      dingLog('getCompanyStyle.fail', { apiUrl, message: err && err.message }, { isError: true })
      throw err
    })
  },

  configJs(company, url) {
    const signUrl = url || getSignUrl()
    const apiPath = '/main/dingtalk/getJsConfig'
    const apiUrl = getFullApiUrl(apiPath)
    dingLog('configJs.request', {
      apiUrl,
      corpId: company.corpId,
      domain: getWebsiteUrl(),
      signUrl
    })
    return new Promise((resolve, reject) => {
      Util.ajax({
        data: {
          corpId: company.corpId,
          domain: getWebsiteUrl(),
          url: encodeURIComponent(signUrl)
        },
        url: Util.getApiUrl(apiPath)
      }, (cfg) => {
        dingLog('configJs.response', { apiUrl, data: cfg })
        window.dd.config({
          agentId: cfg.agentId,
          corpId: cfg.corpId,
          timeStamp: cfg.timeStamp,
          nonceStr: cfg.nonceStr,
          signature: cfg.signature,
          jsApiList: ['runtime.permission.requestAuthCode']
        })
        window.dd.ready(() => {
          dingLog('dd.ready')
          resolve(company)
        })
        window.dd.error((e) => {
          dingLog('dd.error', e, { isError: true })
          console.error(e)
          reject(new Error('钉钉 JSAPI 配置失败'))
        })
      }, true).catch((err) => {
        dingLog('configJs.fail', { apiUrl, message: err && err.message }, { isError: true })
        reject(err)
      })
    })
  },

  requestAuthCode(company) {
    return new Promise((resolve, reject) => {
      dingLog('requestAuthCode.start', { corpId: company.corpId })
      window.dd.runtime.permission.requestAuthCode({
        corpId: company.corpId,
        onSuccess(res) {
          dingLog('requestAuthCode.success', { hasCode: !!res.code })
          resolve(res.code)
        },
        onFail(err) {
          dingLog('requestAuthCode.fail', err, { isError: true })
          Util.info('钉钉授权失败', 'error')
          reject(err || new Error('钉钉授权失败'))
        }
      })
    })
  },

  loginByDingTalk(callback) {
    const finish = (data) => {
      if (callback) callback(data)
      return data
    }

    if (!this.isDingTalk()) {
      dingLog('loginByDingTalk.skip', 'not in dingtalk container')
      return Promise.resolve(finish(null))
    }

    DingDebugPanel.clearSession()
    DingDebugPanel.setPageContext({
      href: location.href,
      signUrl: getSignUrl(),
      host: location.host,
      websiteUrl: getWebsiteUrl()
    })
    dingLog('loginByDingTalk.start')

    return this.getCompanyStyle()
      .then((company) => {
        if (!company || !company.corpId) {
          dingLog('loginByDingTalk.noCorpId', company, { isError: true })
          Util.info('未配置钉钉集成', 'warning')
          throw new Error('未配置钉钉集成')
        }
        const signUrl = getSignUrl()
        DingDebugPanel.setPageContext({
          href: location.href,
          signUrl,
          host: location.host,
          websiteUrl: getWebsiteUrl()
        })
        return ensureDd()
          .then(() => this.configJs(company, signUrl))
          .then((c) => this.requestAuthCode(c).then((code) => ({ code, company: c })))
          .then(({ code, company }) => {
            const apiPath = '/main/dingtalk/login'
            const apiUrl = getFullApiUrl(apiPath)
            dingLog('dingtalk.login.request', {
              apiUrl,
              corpId: company.corpId,
              domain: getWebsiteUrl(),
              hasAuthCode: !!code
            })
            return Util.ajax({
              data: {
                authCode: code,
                corpId: company.corpId,
                domain: getWebsiteUrl()
              },
              url: Util.getApiUrl(apiPath)
            }, null, true)
          })
          .then((data) => {
            const apiUrl = getFullApiUrl('/main/dingtalk/login')
            dingLog('dingtalk.login.response', {
              apiUrl,
              hasToken: !!(data && data.token),
              accountId: data && data.account && data.account.id,
              data
            })
            if (!data || !data.token) {
              throw new Error('钉钉登录失败')
            }
            localStorage.setItem(Config.tokenKey, data.token)
            localStorage.setItem(Config.accountKey, JSON.stringify(data.account))
            if (isDebugEnabled()) {
              DingDebugPanel.flush(true)
            }
            return finish(data)
          })
      })
      .catch((err) => {
        const summary = {
          message: err && err.message,
          websiteUrl: getWebsiteUrl(),
          host: location.host
        }
        dingLog('loginByDingTalk.fail', summary, { isError: true })
        DingDebugPanel.flush(true)

        if (err && err.message === '无法识别企业域名') {
          Util.info(err.message, 'warning')
        } else if (err && err.message === '钉钉 JSAPI 配置失败') {
          Util.info(err.message, 'error')
        } else if (err && err.message && err.message !== '未配置钉钉集成') {
          console.error(err)
        }
        return finish(null)
      })
  }
}

export default Dingtalk
